import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { onValue, ref, update } from "firebase/database";
import { db } from "../firebaseConfig";
import { useAuthStore } from "../stores/useAuthStore.jsx";
import { usePanelsStore } from "../stores/usePanelsStore.jsx";
import { useCan } from "../hooks/useCan.js";

const escapeHtml = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const friendChatId = (a, b) => `friend_${[a, b].sort().join("_")}`;

const createFriendMarkerIcon = ({ name, photoURL }) => {
  const initial = escapeHtml(String(name || "T").slice(0, 1).toUpperCase());
  return L.divIcon({
    className: "tk-friend-live-marker",
    html: `<div><span>${initial}</span>${
      photoURL ? `<img src="${escapeHtml(photoURL)}" alt="" />` : ""
    }</div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
};

const installMarkerImageFallback = (marker) => {
  const image = marker?.getElement?.()?.querySelector?.("img");
  if (!image || image.dataset.fallbackReady === "1") return;
  image.dataset.fallbackReady = "1";
  const revealInitial = () => image.remove();
  image.addEventListener("error", revealInitial, { once: true });
  if (image.complete && image.naturalWidth === 0) revealInitial();
};

export default function MapViewFriendSharesOverlay({ map }) {
  const user = useAuthStore((state) => state.user);
  const canReceive = useCan("PanelNav", "share_with_friends");
  const [shares, setShares] = useState({});
  const markersRef = useRef({});

  useEffect(() => {
    if (!user?.uid || !canReceive) {
      setShares({});
      return undefined;
    }
    const shareUnsubs = new Map();
    const stopInbox = onValue(ref(db, `liveShareInbox/${user.uid}`), (snapshot) => {
      const inbox = snapshot.val() || {};
      const wanted = new Set(Object.keys(inbox));

      shareUnsubs.forEach((stop, shareId) => {
        if (wanted.has(shareId)) return;
        stop();
        shareUnsubs.delete(shareId);
        setShares((current) => {
          const next = { ...current };
          delete next[shareId];
          return next;
        });
      });

      Object.entries(inbox).forEach(([shareId, meta]) => {
        if (shareUnsubs.has(shareId)) return;
        const stop = onValue(ref(db, `liveShares/${shareId}`), (shareSnapshot) => {
          const value = shareSnapshot.val();
          setShares((current) => {
            const next = { ...current };
            if (!value?.active || !value?.position || value?.recipients?.[user.uid] !== true) {
              delete next[shareId];
            } else {
              next[shareId] = { id: shareId, ...meta, ...value };
            }
            return next;
          });
        });
        shareUnsubs.set(shareId, stop);
      });
    });

    return () => {
      stopInbox();
      shareUnsubs.forEach((stop) => stop());
    };
  }, [canReceive, user?.uid]);

  useEffect(() => {
    if (!map || !canReceive) return undefined;
    const currentIds = new Set(Object.keys(shares));

    Object.values(shares).forEach((share) => {
      const lat = Number(share?.position?.lat);
      const lon = Number(share?.position?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const ownerUid = share?.user?.uid || share?.ownerUid;
      const name = share?.user?.name || share?.ownerName || "Trekker";
      const photoURL = share?.user?.photoURL || share?.ownerPhotoURL || "";
      const iconSignature = `${photoURL}\n${name}`;
      let marker = markersRef.current[share.id];

      if (!marker) {
        marker = L.marker([lat, lon], {
          icon: createFriendMarkerIcon({ name, photoURL }),
          zIndexOffset: 750,
        }).addTo(map);
        marker.__trekkinFriendIconSignature = iconSignature;
        markersRef.current[share.id] = marker;
      } else {
        marker.setLatLng([lat, lon]);
        if (marker.__trekkinFriendIconSignature !== iconSignature) {
          marker.setIcon(createFriendMarkerIcon({ name, photoURL }));
          marker.__trekkinFriendIconSignature = iconSignature;
        }
      }
      installMarkerImageFallback(marker);

      const ageMinutes = Math.max(0, Math.floor((Date.now() - Number(share.lastUpdate || share.position?.ts || Date.now())) / 60000));
      const actionId = `tk-friend-message-${share.id}`;
      marker.bindPopup(
        `
          <div class="tk-friend-live-popup">
            ${photoURL ? `<img src="${escapeHtml(photoURL)}" alt="" />` : ""}
            <div><b>${escapeHtml(name)}</b><small>${ageMinutes < 1 ? "Agora" : `Há ${ageMinutes} min`}</small></div>
            <button id="${escapeHtml(actionId)}" type="button">Enviar mensagem</button>
          </div>
        `,
        { className: "tk-friend-live-leaflet-popup" }
      );
      marker.off("popupopen");
      marker.on("popupopen", () => {
        const button = document.getElementById(actionId);
        if (!button || !ownerUid) return;
        button.onclick = async () => {
          const chatId = friendChatId(user.uid, ownerUid);
          const [userId, guideId] = [user.uid, ownerUid].sort();
          const now = Date.now();
          await update(ref(db), {
            [`directChats/${chatId}/userId`]: userId,
            [`directChats/${chatId}/guideId`]: guideId,
            [`directChats/${chatId}/friendChat`]: true,
            [`directChats/${chatId}/ended`]: false,
            [`directChats/${chatId}/updatedAt`]: now,
            [`userDirectChats/${user.uid}/${chatId}`]: {
              otherUid: ownerUid,
              friendChat: true,
              updatedAt: now,
            },
          });
          map.closePopup();
          usePanelsStore.getState().openPanel("directChat", { chatId });
        };
      });
    });

    Object.entries(markersRef.current).forEach(([shareId, marker]) => {
      if (currentIds.has(shareId)) return;
      map.removeLayer(marker);
      delete markersRef.current[shareId];
    });

    return () => undefined;
  }, [canReceive, map, shares, user?.uid]);

  useEffect(() => () => {
    if (!map) return;
    Object.values(markersRef.current).forEach((marker) => map.removeLayer(marker));
    markersRef.current = {};
  }, [map]);

  useEffect(() => {
    if (canReceive || !map) return;
    Object.values(markersRef.current).forEach((marker) => map.removeLayer(marker));
    markersRef.current = {};
  }, [canReceive, map]);

  return null;
}
